import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { api, ApiError } from './api'
import { ArmsError } from './arms'
import * as session from './session'
import type { PortalIdentity } from '@/types/api'

/** Where ARMS sends the browser back to. Registered on the portal client as a redirect URI. */
export const CALLBACK_PATH = '/auth/callback'

interface AuthState {
  partner: PortalIdentity | undefined
  /** True until the session has been restored, so nothing flashes the sign-in screen. */
  restoring: boolean
  /**
   * Codemax authenticated the caller and the portal will not have them: not invited, no
   * longer active, or a member of staff at the wrong door. Every one of these is a 403,
   * never a 401 - so it must never be answered with another sign-in attempt, which would
   * fetch the same valid token from the same ARMS cookie and be refused again.
   */
  refusal: ApiError | undefined
  /** Something went wrong reaching Codemax. Distinct from a refusal, which succeeded. */
  error: string | undefined
  signIn: () => Promise<void>
  signOut: () => Promise<void>
  switchAccount: () => Promise<void>
}

const AuthContext = createContext<AuthState | undefined>(undefined)

/**
 * Who is signed in, and which partner they act for.
 *
 * A deliberately smaller thing than the staff application's provider, and the difference
 * is the point: there is no `can`, no `scope` and no country list, because a partner
 * holds no permission code and no country grant. Their whole reach is one foreign key,
 * and the API enforces it on every route rather than trusting anything decided here.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [partner, setPartner] = useState<PortalIdentity | undefined>(undefined)
  const [refusal, setRefusal] = useState<ApiError | undefined>(undefined)
  const [error, setError] = useState<string | undefined>(undefined)
  const [restoring, setRestoring] = useState(true)
  const navigate = useNavigate()

  /**
   * Turns an ARMS token into a portal session.
   *
   * Called on every load rather than only after a sign-in, which is what makes it
   * self-healing: somebody invited after they first tried is bound on their next visit,
   * without anybody at Codemax having to talk them through it.
   */
  const start = useCallback(async () => {
    try {
      const me = await api.post<PortalIdentity>('/portal/session')
      setPartner(me)
      setRefusal(undefined)
    } catch (cause) {
      if (cause instanceof ApiError && cause.isForbidden) {
        setRefusal(cause)
        setPartner(undefined)
        return
      }

      throw cause
    }
  }, [])

  useEffect(() => {
    let live = true

    async function restore() {
      let leaving = false

      try {
        if (window.location.pathname === CALLBACK_PATH) {
          const { returnTo } = await session.completeCallback(window.location.search)

          // Through the router rather than history.replaceState: replacing the entry
          // behind the router's back leaves it still rendering /auth/callback, which is
          // not a route, so a successful sign-in lands on "that page does not exist".
          // Replace rather than push, so Back does not return to a redeemed code.
          navigate(returnTo, { replace: true })

          await start()
          return
        }

        if (session.hasSession()) {
          await start()
          return
        }

        // Nothing in memory, which is every reload - tokens are held in memory and
        // nowhere else. Ask ARMS whether this browser is already signed in; if it is,
        // the answer is a redirect and this page is on its way out.
        leaving = await session.restore()
      } catch (cause) {
        session.clearSession()

        // login_required is not a fault - it is what a first visit looks like, and the
        // sign-in screen is the right answer to it.
        if (cause instanceof ArmsError && !cause.needsInteraction) {
          if (live) setError(cause.message)
        } else if (!(cause instanceof ArmsError)) {
          if (live) setError('Could not reach Codemax sign-in. Check your connection and try again.')
        }
      } finally {
        // Not while the browser is already navigating away: flipping this would flash
        // the sign-in screen on top of a redirect that is about to succeed.
        if (live && !leaving) setRestoring(false)
      }
    }

    void restore()
    return () => {
      live = false
    }
  }, [start, navigate])

  const signIn = useCallback(() => session.signIn(), [])

  const signOut = useCallback(async () => {
    setPartner(undefined)
    setRefusal(undefined)
    setError(undefined)
    await session.signOut()
  }, [])

  const switchAccount = useCallback(async () => {
    setPartner(undefined)
    setRefusal(undefined)
    setError(undefined)
    await session.switchAccount()
  }, [])

  const value = useMemo<AuthState>(
    () => ({ partner, restoring, refusal, error, signIn, signOut, switchAccount }),
    [partner, restoring, refusal, error, signIn, signOut, switchAccount],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside AuthProvider')

  return context
}
